// Grouping helpers shared by ArtifactsView (the renderer) and surfaces that build navigation over
// the same sections (the Clinical Summary Details tab's rail). Separate from the component file so
// fast refresh keeps working there.

// The text labels of an artifact's `group` PATH (e.g. ['Labs', 'Comprehensive Metabolic Panel']).
// `group` is Code[] on the wire; a legacy single-Code group (pre-path threads/overviews) is
// tolerated by wrapping it. Untitled path elements are skipped.
function groupPathTexts(group: unknown): string[] {
  const path = Array.isArray(group) ? group : group ? [group] : []
  return path
    .map((code) => (code as { text?: string | null } | null)?.text)
    .filter((text): text is string => Boolean(text))
}

// One cross-kind section: every artifact (kpi/chart/table) sharing a group path renders together
// under one collapsible, labelled with the path joined as "A / B". Generic over the item types so
// any structurally-compatible artifacts shape (thread entries, tool results, the clinical-summary
// details endpoint) partitions without nominal-type friction.
export type ArtifactSectionOf<K, C, T> = {
  label: string
  // The full group path (text labels, outermost first).
  path: string[]
  // The path's first element — the top-level section a navigation rail links to.
  root: string
  kpis: K[]
  charts: C[]
  tables: T[]
}

export type PartitionedArtifacts<K, C, T> = {
  sections: ArtifactSectionOf<K, C, T>[]
  ungrouped: { kpis: K[]; charts: C[]; tables: T[] }
}

// Partition all artifact kinds into path-keyed sections plus the ungrouped remainder. Section
// order is HIERARCHICAL first-appearance order (scanning kpis, then tables, then charts):
// whichever group path is found first anchors itself AND all of its nested paths — e.g. for
// ['Labs'], ['Vitals'], ['Labs','ABC'] the order is Labs, Labs/ABC, Vitals, regardless of where
// the nested path appears. Within that rule, producers control ordering through their emission
// order (see the clinical-summary converter); the UI must not impose its own.
export function partitionArtifacts<K, C, T>(artifacts: {
  kpis?: K[]
  charts?: C[]
  tables?: T[]
}): PartitionedArtifacts<K, C, T> {
  const sections: ArtifactSectionOf<K, C, T>[] = []
  const byLabel = new Map<string, ArtifactSectionOf<K, C, T>>()
  const ungrouped: PartitionedArtifacts<K, C, T>['ungrouped'] = {
    kpis: [],
    charts: [],
    tables: [],
  }

  const add = <Item>(
    items: Item[] | undefined,
    bucketOf: (section: ArtifactSectionOf<K, C, T>) => Item[],
    ungroupedBucket: Item[],
  ) => {
    for (const item of items ?? []) {
      const parts = groupPathTexts((item as { group?: unknown }).group)
      if (parts.length === 0) {
        ungroupedBucket.push(item)
        continue
      }
      const label = parts.join(' / ')
      let section = byLabel.get(label)
      if (!section) {
        section = { label, path: parts, root: parts[0], kpis: [], charts: [], tables: [] }
        byLabel.set(label, section)
        sections.push(section)
      }
      bucketOf(section).push(item)
    }
  }
  add(artifacts.kpis, (section) => section.kpis, ungrouped.kpis)
  add(artifacts.tables, (section) => section.tables, ungrouped.tables)
  add(artifacts.charts, (section) => section.charts, ungrouped.charts)
  return { sections: orderHierarchically(sections), ungrouped }
}

// Reorder first-appearance sections so every path follows its earliest-seen ancestor: each path
// PREFIX is ranked by when it first appears (walking the sections in appearance order), and
// sections sort by their prefix-rank sequence — parents before children, siblings by first
// appearance. A separator that can't occur in a label keys the prefixes.
const PREFIX_SEPARATOR = '\u0000'
function orderHierarchically<S extends { path: string[] }>(sections: S[]): S[] {
  const prefixRank = new Map<string, number>()
  let nextRank = 0
  const ranksOf = (path: string[], assign: boolean): number[] => {
    const ranks: number[] = []
    let key = ''
    for (const part of path) {
      key = key ? key + PREFIX_SEPARATOR + part : part
      if (assign && !prefixRank.has(key)) prefixRank.set(key, nextRank++)
      ranks.push(prefixRank.get(key) ?? Number.MAX_SAFE_INTEGER)
    }
    return ranks
  }
  for (const section of sections) ranksOf(section.path, true)

  return [...sections].sort((a, b) => {
    const aRanks = ranksOf(a.path, false)
    const bRanks = ranksOf(b.path, false)
    for (let i = 0; i < Math.min(aRanks.length, bRanks.length); i++) {
      if (aRanks[i] !== bRanks[i]) return aRanks[i] - bRanks[i]
    }
    // Equal prefix — the shorter path (the ancestor) leads.
    return aRanks.length - bRanks.length
  })
}

// Anchor id for a section element, so navigation rails can scroll to it. The readable slug alone
// is lossy — labels differing only in punctuation ("LDL-C" vs "LDL C") collapse to the same id, and
// a rail looking one up by `#id` would scroll to whichever rendered first — so a short
// deterministic hash of the EXACT label disambiguates them. Identical labels still map to the same
// id on purpose: one label is one section, and both the renderer and the rail derive the id from it
// independently.
export function sectionAnchorId(label: string): string {
  let hash = 0
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  return `artifact-section-${slug}-${(hash >>> 0).toString(36)}`
}
